import React, { useEffect, useMemo } from 'react';
import badgeSound from '../../assets/sounds/badge.mp3';
import chestDiamondSound from '../../assets/sounds/chest_diamond.mp3';
import chestGoldSound from '../../assets/sounds/chest_gold.mp3';
import levelUpSound from '../../assets/sounds/levelup.mp3';
import successSound from '../../assets/sounds/success.mp3';
import treasureSound from '../../assets/sounds/treasure.mp3';
import xpGainSound from '../../assets/sounds/xp-gain.mp3';
import { CaptainKork } from '../mascot/CaptainKork';
import { getKorkMomentLine, getKorkMomentMood, getKorkMomentVariant } from '../../mascot/captainKork';
import { getKorkMomentAudio } from '../../mascot/captainKorkAudio';
import { isKorkVoiceEnabled, isXpSoundEnabled } from '../../utils/settings';

const SOUND_MAP = {
  achievement: badgeSound,
  fragment: successSound,
  map_complete: treasureSound,
  rare_loot: successSound,
  epic_loot: chestDiamondSound,
  legendary_loot: chestGoldSound,
  level_up: levelUpSound,
  reward: successSound,
  scanner_energy: xpGainSound,
};

function getMomentLabel(moment) {
  switch (moment?.type) {
    case 'map_completed':
      return 'Legendärer Moment';
    case 'map_fragment_found':
      return 'Kartenfragment entdeckt';
    case 'rare_loot_found':
      return 'Seltener Fund';
    case 'epic_loot_found':
      return 'Epischer Fund';
    case 'pfand_beifang_found':
      return 'Pfand-Beifang';
    case 'level_up':
      return 'Level-Up';
    case 'achievement':
      return 'Achievement freigeschaltet';
    default:
      return 'Pfandpirat-Moment';
  }
}

function getSoundUrl(moment) {
  if (moment?.soundUrl) return moment.soundUrl;
  if (moment?.sound && SOUND_MAP[moment.sound]) return SOUND_MAP[moment.sound];
  if (moment?.rarity === 'legendary') return SOUND_MAP.legendary_loot;
  if (moment?.rarity === 'epic') return SOUND_MAP.epic_loot;
  if (moment?.rarity === 'rare') return SOUND_MAP.rare_loot;
  return '';
}

function RewardPill({ children }) {
  return <span className="pfp-moment-reward-pill">{children}</span>;
}

function Rewards({ moment }) {
  const hasXp = Number(moment?.rewardXp || 0) > 0;
  const hasCoins = Number(moment?.rewardCoins || 0) > 0;
  const hasFragments = Number(moment?.rewardFragments || 0) > 0;
  const items = Array.isArray(moment?.rewardLoot) ? moment.rewardLoot : [];

  if (!hasXp && !hasCoins && !hasFragments && !items.length) {
    return null;
  }

  return (
    <div className="pfp-moment-rewards">
      {hasXp ? <RewardPill>+{Number(moment.rewardXp)} XP</RewardPill> : null}
      {hasCoins ? <RewardPill>+{moment.rewardCoinLabel || `${Number(moment.rewardCoins)} Bronzemünze${Number(moment.rewardCoins) === 1 ? '' : 'n'}`}</RewardPill> : null}
      {hasFragments ? <RewardPill>+{Number(moment.rewardFragments)} Fragment{Number(moment.rewardFragments) === 1 ? '' : 'e'}</RewardPill> : null}
      {items.map((item) => (
        <RewardPill key={`${item.title}-${item.imageUrl || item.icon || ''}`}>
          {item.imageUrl ? <img src={item.imageUrl} alt="" loading="lazy" /> : item.icon ? <span>{item.icon}</span> : null}
          {item.title}
        </RewardPill>
      ))}
    </div>
  );
}

function MomentVisual({ moment, large = false }) {
  const imageUrl = String(moment?.imageUrl || '').trim();

  return (
    <div className={`pfp-moment-visual ${large ? 'is-large' : ''} animation-${moment?.animation || 'loot_reveal'}`}>
      {moment?.animation === 'map_assemble' ? (
        <div className="pfp-moment-map-fragments" aria-hidden="true">
          <i />
          <i />
          <i />
          <i />
          <i />
          <i />
        </div>
      ) : null}
      {imageUrl ? (
        <img src={imageUrl} alt={moment?.title || ''} loading="eager" />
      ) : (
        <span>{moment?.icon || '🎁'}</span>
      )}
    </div>
  );
}

function Particles({ enabled }) {
  if (!enabled) return null;
  return (
    <div className="pfp-moment-particles" aria-hidden="true">
      {Array.from({ length: 18 }).map((_, index) => <i key={index} />)}
    </div>
  );
}

export default function MomentHost({ moment, onClose }) {
  const presentation = moment?.presentation || 'toast';
  const isFullscreen = presentation === 'fullscreen';
  const isPopup = presentation === 'popup';
  const isToast = presentation === 'toast';
  const hasParticles = moment?.rarity === 'epic' || moment?.rarity === 'legendary' || isFullscreen;
  const korkMood = getKorkMomentMood(moment);
  const korkLine = getKorkMomentLine(moment);
  const korkVariant = getKorkMomentVariant(moment);

  const soundUrl = useMemo(() => getSoundUrl(moment), [moment]);
  const korkAudioUrl = useMemo(() => getKorkMomentAudio(moment), [moment]);

  useEffect(() => {
    if (!moment) return undefined;

    let timer = null;
    let audio = null;
    let voiceAudio = null;
    let voiceTimer = null;

    const autoHideMs = Number(moment.autoHideMs || 0);
    if (autoHideMs > 0) {
      timer = window.setTimeout(() => onClose?.(), autoHideMs);
    }

    if (soundUrl && isXpSoundEnabled()) {
      try {
        audio = new window.Audio(soundUrl);
        audio.volume = isFullscreen ? 0.48 : 0.32;
        audio.play().catch(() => {});
      } catch (error) {}
    }

    if (korkAudioUrl && isKorkVoiceEnabled()) {
      voiceTimer = window.setTimeout(() => {
        try {
          voiceAudio = new window.Audio(korkAudioUrl);
          voiceAudio.volume = isFullscreen ? 0.9 : 0.82;
          voiceAudio.play().catch(() => {});
        } catch (error) {}
      }, soundUrl ? 260 : 120);
    }

    try {
      if (window.navigator?.vibrate) {
        if (isFullscreen) window.navigator.vibrate([160, 80, 180, 80, 120]);
        else if (isPopup) window.navigator.vibrate([90, 60, 90]);
        else window.navigator.vibrate(60);
      }
    } catch (error) {}

    return () => {
      if (timer) window.clearTimeout(timer);
      if (audio) {
        try {
          audio.pause();
        } catch (error) {}
      }
      if (voiceTimer) {
        window.clearTimeout(voiceTimer);
      }
      if (voiceAudio) {
        try {
          voiceAudio.pause();
        } catch (error) {}
      }
      try {
        if (window.navigator?.vibrate) window.navigator.vibrate(0);
      } catch (error) {}
    };
  }, [isFullscreen, isPopup, korkAudioUrl, moment, onClose, soundUrl]);

  if (!moment) return null;

  if (isToast) {
    return (
      <div className={`pfp-moment-toast rarity-${moment.rarity || 'common'}`} role="status" aria-live="polite">
        <MomentVisual moment={moment} />
        <div className="pfp-moment-toast__copy">
          <strong>{moment.title}</strong>
          {moment.text ? <span>{moment.text}</span> : null}
          <div className="pfp-moment-kork">
            <CaptainKork mood={korkMood} motion="pop" size="tiny" variant="head" label="Kaept'n Kork" />
            <div className="pfp-kork-speech">{korkLine}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`pfp-moment-overlay ${isFullscreen ? 'is-fullscreen' : 'is-popup'} rarity-${moment.rarity || 'common'}`} onClick={onClose}>
      <Particles enabled={hasParticles} />
      <div className="pfp-moment-card" onClick={(event) => event.stopPropagation()}>
        <div className="pfp-moment-kicker">{getMomentLabel(moment)}</div>
        <MomentVisual moment={moment} large={isFullscreen} />
        <div className="pfp-moment-kork">
          <CaptainKork mood={korkMood} motion={isFullscreen ? 'celebrate' : 'pop'} size="small" variant={korkVariant} label="Kaept'n Kork" />
          <div className="pfp-kork-speech">{korkLine}</div>
        </div>
        <h2>{moment.title}</h2>
        {moment.text ? <p>{moment.text}</p> : null}
        <Rewards moment={moment} />
        <button type="button" className="pfp-moment-close" onClick={onClose}>
          Weiter
        </button>
      </div>
    </div>
  );
}
